import { useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { InternalDocumentType } from 'store/documents/documents.types'
import { useUploadDocumentMutation } from 'store/documents/documents.api'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

export const EQUIPMENT_DOCUMENT_SUGGESTIONS = [
  'Registration',
  'Insurance',
  'Annual Inspection',
]

type UploadDocumentDialogProps = {
  open: boolean
  onClose: () => void
  associateId: string
  internalType: InternalDocumentType
  documentTypeSuggestions?: string[]
}

const UploadDocumentDialog = ({
  open,
  onClose,
  associateId,
  internalType,
  documentTypeSuggestions = [],
}: UploadDocumentDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [documentType, setDocumentType] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [fileError, setFileError] = useState('')
  const [formError, setFormError] = useState('')

  const [uploadDocument, { isLoading }] = useUploadDocumentMutation()

  const suggestions = useMemo(
    () => (documentTypeSuggestions.length > 0 ? documentTypeSuggestions : []),
    [documentTypeSuggestions]
  )

  const resetForm = () => {
    setSelectedFile(null)
    setDocumentType('')
    setExpiryDate('')
    setFileError('')
    setFormError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    if (isLoading) return
    resetForm()
    onClose()
  }

  const validateFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'File size must not exceed 10MB.'
    }
    return ''
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setSelectedFile(null)
      return
    }
    const error = validateFile(file)
    setFileError(error)
    setSelectedFile(error ? null : file)
  }

  const handleUpload = async () => {
    setFormError('')

    if (!selectedFile) {
      setFileError('Document file is required.')
      return
    }
    if (!documentType.trim()) {
      setFormError('Document type is required.')
      return
    }

    try {
      await uploadDocument({
        file: selectedFile,
        associateId,
        internalType,
        documentType: documentType.trim(),
        expiryDate: expiryDate || undefined,
      }).unwrap()
      resetForm()
      onClose()
    } catch (error: any) {
      const message =
        error?.data?.error?.message ||
        error?.data?.message ||
        'Failed to upload document. Please try again.'
      setFormError(message)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        Upload Document
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Document File <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: fileError ? 'error.main' : 'grey.300',
                borderRadius: '6px',
                overflow: 'hidden',
                bgcolor: 'common.white',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  px: 1.5,
                  py: 1.25,
                  color: selectedFile ? 'text.primary' : 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedFile?.name || 'No file chosen'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  borderRadius: 0,
                  borderLeft: '1px solid',
                  borderColor: 'grey.300',
                  textTransform: 'none',
                  minWidth: 100,
                }}
              >
                Browse
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleFileChange}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              Max 10MB
            </Typography>
            {fileError && (
              <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                {fileError}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Document Type <Typography component="span" color="error.main">*</Typography>
            </Typography>
            {suggestions.length > 0 ? (
              <Autocomplete
                freeSolo
                options={suggestions}
                value={documentType}
                onChange={(_event, value) => setDocumentType(value ?? '')}
                onInputChange={(_event, value) => setDocumentType(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter document type"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: 'common.white' },
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                placeholder="Enter document type"
                value={documentType}
                onChange={(event) => setDocumentType(event.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: 'common.white' },
                }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              {suggestions.length > 0
                ? 'e.g., Registration, Insurance, Annual Inspection'
                : 'e.g., License, Certificate, Permit, Report'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Expiry Date
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={expiryDate}
              onChange={(event) => setExpiryDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: 'common.white' },
              }}
            />
          </Box>

          {formError && (
            <Typography variant="body2" color="error.main">
              {formError}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={isLoading}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {isLoading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadDocumentDialog
